import { paths, components } from "@securityjourney/svc-code-sub/src/gen/code_sub";
import {
  ServiceClient,
  TokenFunc,
  ServiceEventSource,
  Call,
  ServiceError,
} from "@securityjourney/service-client";

export type CodeSubmission = components["schemas"]["securityjourney.proto.code_sub.CodeSubmission"];
export type RetrieveCodeSubmissionsRequest =
  components["schemas"]["securityjourney.proto.code_sub.RetrieveCodeSubmissionRequest"];
export type CodeSubmissions = {
  userCodeSubmissions?: components["schemas"]["securityjourney.proto.code_sub.CodeSubmission"][];
};
export type CodeSubTestResult = components["schemas"]["securityjourney.proto.code_sub.TestResult"];
export type CodeSubVolume = components["schemas"]["securityjourney.proto.code_sub.Volume"];

export class CodeSubClient extends ServiceClient<paths> {
  stream = null as ServiceEventSource | null;

  constructor(getToken: TokenFunc, url?: string) {
    super({ getToken, url });
  }

  retrieveCodeSubmission(req: RetrieveCodeSubmissionsRequest): Call<CodeSubmissions> {
    const func = this.fetcher.path("/svc/code-sub/submissions").method("get").create();
    const args = {
      lessonId: req.lessonId,
      lessonVersion: req.lessonVersion,
      limit: req.limit,
      since: req.since,
      until: req.until,
    };

    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init => func(args, init))
        .then(res => res.data)
        .catch(ServiceError.raise),
    };
  }

  /**
   * Opens a stream to begin receiving data about code submissions made by the
   * authorized user for the given lesson. When the connection is first established, an
   * initial set of historical submisisons will be received and passed to the
   * onSubmission callback. As new submissions are completed, they will be passed to
   * onSubmission as well. Any errors that occur during onSubmission, as well as any
   * fatal errors in the event stream connection, will be passed to onError.
   *
   * Call closeStream() when finished. Only one stream may be open at any given time.
   */
  openStream(
    lessonId: string,
    lessonVersion: number,
    onSubmission: (submission: CodeSubmission) => void,
    onError: (err: unknown) => void,
    since?: Date
  ) {
    since = since || new Date(0);

    if (this.stream) {
      throw new Error("CodeSubClient already has a stream open");
    }

    const urlSuffix = `/svc/code-sub/submissions/${lessonId}/v/${lessonVersion}/stream?since=${since}`;
    const url = this.baseUrl + urlSuffix;
    this.stream = new ServiceEventSource(url, {
      getToken: this.getToken || undefined,
      maxReconnectAttempts: 10,
      connectionRetryBackoff: i => {
        if (i < 3) return 100;
        if (i < 5) return 1000;
        return 5000;
      },
      onEvent: ev => {
        try {
          const submission = JSON.parse(ev.data) as CodeSubmission;
          onSubmission(submission);
        } catch (err) {
          onError(err);
        }
      },
      onReconnectAttemptsExhausted: err => {
        onError(err);
      },
    });
    this.stream.start();
  }

  /**
   * Closes any event stream connection previously opened via openStream().
   */
  closeStream() {
    if (this.stream) {
      this.stream.stop();
      this.stream = null;
    }
  }
}
